import React from 'react';
import { questionsApi } from '../api/questionsApi';
import { detailToEditorState, emptyEditorState, editorStateToRequest } from '../api/questionMappers';
import { BRANCHING_ENABLED_TYPES } from '../constants';
import type { QuestionEditorState, QuestionDetail } from '../types';

export function useQuestionEditor(questionId?: number): {
  question: QuestionDetail | null;
  editorState: QuestionEditorState;
  setEditorState: React.Dispatch<React.SetStateAction<QuestionEditorState>>;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  error: Error | null;
  save: (options?: { configureBranching?: boolean; surveyId?: number }) => Promise<{
    questionId: number;
    nextPath?: string;
  }>;
} {
  const [question, setQuestion] = React.useState<QuestionDetail | null>(null);
  const [editorState, setEditorState] = React.useState<QuestionEditorState>(emptyEditorState);
  const initialStateRef = React.useRef<string>(JSON.stringify(emptyEditorState()));
  const [isLoading, setIsLoading] = React.useState(!!questionId);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  // isDirty: for edit mode compare against the loaded snapshot; new questions are always dirty
  const isDirty = !questionId || JSON.stringify(editorState) !== initialStateRef.current;

  React.useEffect(() => {
    if (!questionId) {
      setEditorState(emptyEditorState());
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    questionsApi
      .getQuestion(questionId)
      .then((res) => {
        if (!cancelled) {
          setQuestion(res.question);
          const loaded = detailToEditorState(res.question);
          initialStateRef.current = JSON.stringify(loaded);
          setEditorState(loaded);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [questionId]);

  const save = React.useCallback(
    async (options?: { configureBranching?: boolean; surveyId?: number }) => {
      setIsSaving(true);
      try {
        const payload = editorStateToRequest(editorState);
        let savedId: number;

        if (questionId) {
          await questionsApi.updateQuestion(questionId, payload);
          savedId = questionId;
        } else {
          const res = await questionsApi.createQuestion(payload);
          savedId = res.questionId;
        }

        const typeId = editorState.questionTypeId;
        let nextPath: string | undefined;

        if (options?.configureBranching && typeId && BRANCHING_ENABLED_TYPES.has(typeId)) {
          const qs = options.surveyId ? `?surveyId=${options.surveyId}` : '';
          nextPath = `/admin/questions/${savedId}/branching${qs}`;
        }

        return { questionId: savedId, nextPath };
      } finally {
        setIsSaving(false);
      }
    },
    [questionId, editorState],
  );

  return { question, editorState, setEditorState, isLoading, isSaving, isDirty, error, save };
}
